import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import { Globals } from "../globals";
import Container from "react-bootstrap/esm/Container";
import Card from "react-bootstrap/esm/Card";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import AppFooter from "../footer/AppFooter";
import MyPlanSlotForm, { MyPlanService, MyPlanSlotInput } from "./MyPlanSlotForm";
import MyPlanActions, { Colleague } from "./MyPlanActions";
import MyPlanAbsences from "./MyPlanAbsences";
import MyPlanCalendar from "./MyPlanCalendar";
import MyPlanConsent from "./MyPlanConsent";
import { applyGemeindeLanguage } from "../i18n/gemeindeLanguage";
import { Absence, findAbsence } from "../utils/absences";

interface MyPlanData {
    preacherName: string;
    gemeindeName: string;
    /** The Gemeinde's default language (GOD-50); missing from older functions. */
    gemeindeLanguage?: string;
    services: MyPlanService[];
    colleagues: Colleague[];
    absences?: Absence[];
    consents?: { email: boolean; whatsapp: boolean };
    hasEmail?: boolean;
    hasPhone?: boolean;
}

const STATUS_BADGE: Record<MyPlanService["status"], { className: string; label: string }> = {
    open: { className: "gl-badge--warn", label: "myPlan.statusOpen" },
    done: { className: "gl-badge--ok", label: "myPlan.statusDone" },
    declined: { className: "gl-badge--danger", label: "myPlan.statusDeclined" },
    "swap-pending": { className: "gl-badge--accent", label: "myPlan.statusSwapPending" },
};

const slotId = (service: MyPlanService) => `${service.serviceId}-${service.slot}`;

/**
 * Public page /mein-plan/:token: a preacher's own upcoming services. The token
 * in the link is the only credential – there is no login.
 */
export default function MyPlanPage() {
    const { token } = useParams<{ token: string }>();
    const { t } = useTranslation();
    const [plan, setPlan] = useState<MyPlanData | null>(null);
    const [state, setState] = useState<"loading" | "ready" | "invalid" | "error">("loading");
    const [openId, setOpenId] = useState<string | null>(null);
    // The slot something was just saved for, and what to say about it.
    const [savedId, setSavedId] = useState<string | null>(null);
    const [savedMessage, setSavedMessage] = useState("");

    const load = useCallback(async () => {
        const result = await httpsCallable<{ token: string }, MyPlanData>(functions, "getMyPlan")({ token: token ?? "" });
        applyGemeindeLanguage(result.data.gemeindeLanguage);
        setPlan(result.data);
        setState("ready");
    }, [token]);

    useEffect(() => {
        document.title = t("myPlan.title");
    }, [t]);

    useEffect(() => {
        load().catch((err: { code?: string }) => setState(err.code === "functions/not-found" ? "invalid" : "error"));
    }, [load]);

    const formatDate = (isoDate: string) =>
        new Date(isoDate).toLocaleDateString(t("calendar.locale") as string, {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "UTC",
        });

    const roleLabel = (service: MyPlanService) =>
        service.mode === "intro"
            ? t("sermonInfo.introSlot")
            : service.mode === "reminder"
                ? t("sermonInfo.reminderSlot")
                : service.slot === 1
                    ? t("sermonInfo.firstSermon")
                    : t("sermonInfo.secondSermon");

    const handleSubmit = async (service: MyPlanService, input: MyPlanSlotInput) => {
        await httpsCallable(functions, "submitMySlot")({
            token,
            serviceId: service.serviceId,
            slot: service.slot,
            ...input,
        });
        await finish(service, t("myPlan.saved") as string);
    };

    const finish = async (service: MyPlanService, message: string) => {
        await load();
        setOpenId(null);
        setSavedId(slotId(service));
        setSavedMessage(message);
    };

    if (state === "loading") {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
                <p className="mt-2">{t("common.loading")}</p>
            </Container>
        );
    }

    if (state !== "ready" || !plan) {
        return (
            <Container className="mt-5" style={{ maxWidth: "600px" }}>
                <Alert variant="danger">
                    <Alert.Heading>{t("myPlan.invalidTitle")}</Alert.Heading>
                    <p>{t(state === "invalid" ? "myPlan.invalidText" : "errors.anErrorOccurred")}</p>
                    <Link to={Globals.PATH_MY_PLAN}>{t("myPlan.requestNewLink")}</Link>
                </Alert>
            </Container>
        );
    }

    // The preacher's own absences, to flag services that fall into one.
    const ownAbsences = new Map(plan.absences?.length ? [["me", plan.absences]] : []);

    return (
        <>
            <Container className="mt-4" style={{ maxWidth: "640px" }}>
                <div className="gl-meta">{plan.gemeindeName}</div>
                <h3 className="mb-1">{t("myPlan.heading")}</h3>
                <p className="text-muted">{plan.preacherName}</p>

                {plan.services.length === 0 && <Alert variant="info">{t("myPlan.empty")}</Alert>}

                {plan.services.map((service) => {
                    const id = slotId(service);
                    const isOpen = openId === id;
                    return (
                        <Card className="mb-3" key={id}>
                            <button
                                type="button"
                                className="btn text-start w-100 p-3 border-0"
                                aria-expanded={isOpen}
                                onClick={() => {
                                    setOpenId(isOpen ? null : id);
                                    setSavedId(null);
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                    <div>
                                        <div className="fw-semibold">{formatDate(service.date)}</div>
                                        <div className="text-muted">
                                            {service.serviceLabel} · {roleLabel(service)}
                                        </div>
                                    </div>
                                    <span className={`gl-badge flex-shrink-0 ${STATUS_BADGE[service.status].className}`}>
                                        {t(STATUS_BADGE[service.status].label)}
                                    </span>
                                </div>
                                {(service.topic || service.biblePassage) && (
                                    <div className="mt-2 small">
                                        {[service.topic, service.biblePassage].filter(Boolean).join(" · ")}
                                    </div>
                                )}
                                {ownAbsences.size > 0 && findAbsence(ownAbsences, "me", service.date.slice(0, 10)) && service.status !== "declined" && (
                                    <div className="mt-1 small text-danger">{t("myPlan.absenceConflict")}</div>
                                )}
                                {service.otherPreacherName && (
                                    <div className="mt-1 small text-muted">
                                        {t("myPlan.together", { name: service.otherPreacherName })}
                                    </div>
                                )}
                            </button>
                            {savedId === id && (
                                <Alert variant="success" className="mx-3 mb-3">
                                    {savedMessage}
                                </Alert>
                            )}
                            {isOpen && (
                                <Card.Body className="border-top">
                                    {service.status === "declined" ? (
                                        <p className="mb-0">{t("myPlan.declinedInfo")}</p>
                                    ) : (
                                        <>
                                            <MyPlanSlotForm service={service} onSubmit={(input) => handleSubmit(service, input)} />
                                            <MyPlanActions
                                                token={token ?? ""}
                                                service={service}
                                                colleagues={plan.colleagues ?? []}
                                                formatDate={formatDate}
                                                onDone={(message) => finish(service, message)}
                                            />
                                        </>
                                    )}
                                </Card.Body>
                            )}
                        </Card>
                    );
                })}

                <MyPlanAbsences
                    token={token ?? ""}
                    absences={plan.absences ?? []}
                    onChange={(absences) => setPlan({ ...plan, absences })}
                />
                <MyPlanCalendar token={token ?? ""} />
                <MyPlanConsent
                    token={token ?? ""}
                    consents={plan.consents ?? { email: false, whatsapp: false }}
                    hasEmail={!!plan.hasEmail}
                    hasPhone={!!plan.hasPhone}
                    onChange={(consents) => setPlan({ ...plan, consents })}
                />

                <p className="small text-muted mt-4">{t("myPlan.linkHint")}</p>
            </Container>
            <AppFooter />
        </>
    );
}
